import { OfferDetailsMixed, OfferDetailsSingle } from ".";
import { useCallback, useEffect, useState } from "react";

import { OfferDetailsGiftCard } from "./OfferDetailsGiftCard";
import { VinoDataOffer } from "../../types/vino-data-types";
import { offerDetailsGiftCardPropsFromVinoDataOffer } from "../../transformers/vino-data/offer-details-gift-card";
import { offerDetailsMixedPropsFromVinoDataOffer } from "../../transformers/vino-data/offer-details-mixed";
import { offerDetailsSinglePropsFromVinoDataOffer } from "../../transformers/vino-data/offer-details-single";
import { trackOfferViewEvent } from "../../utils/event-tracking";
import useAuth from "../../hooks/use-auth";
import { useLocation } from "@reach/router";
import { useProfile } from "../../hooks/account/use-profile";
import PayWall from "../pay-wall/PayWall";
import { trackEvent } from "../../utils/track-event";
import { EventType } from "../../types";
import { ProductPreloader } from "../preloaders/ProductPreloader";
import { isUserAStaff } from "../../utils/loyalty";
import config from "../../../config.json";
import process from "process";

export interface OfferDetailsProps {
  offer: VinoDataOffer;
  refresh?: any;
}

export const OfferDetails = ({
  offer,
  refresh,
}: OfferDetailsProps): JSX.Element => {
  const { sku } = offer;
  const location = useLocation();
  const { user, isLoading: authLoading, isAuthenticated, join } = useAuth();

  const [isEligible, setIsEligible] = useState(!offer.tiersEligible || offer.tiersEligible?.includes("ALL") || offer.tiersEligible?.includes("GUEST"));
  const [modal, setModal] = useState<any>(<ProductPreloader />);
  const tierMap = config[process.env.GATSBY_VM_MARKET]["TIER_MAP"] || {};

  useEffect(() => {
    if (
      !offer.tiersEligible ||
      offer.tiersEligible?.includes("ALL") ||
      offer.tiersEligible?.includes("GUEST")
    ) {
      setIsEligible(true);
      return;
    }
    if (authLoading) {
      return;
    }
    if (!isAuthenticated) {
      setModal(
        <PayWall
          title={"Login to see all of your exclusive VIM deals."}
          dealTitle={
            "Here are other offers you might consider"
          }
          offer={offer}
          LoginButton={true}
          checkStatus={false}
          cause={"guest_cannot_see"}
          theme="modal"
          category="Offer"
          page_tier={offer.tiersEligible.toString()}
          user_tier={"GUEST"}
        />
      );
      setIsEligible(false);
      return;
    }

    if (!user?.customer_group) {
      return;
    }

    if (user && isUserAStaff(user)) {
      setIsEligible(true);
      return;
    }
    
    const isCustomerEligible = offer.tiersEligible
      .map((tier) => {
        return (
          (tierMap[user?.customer_group?.name?.toUpperCase() || 'GREEN']) >=
          (tierMap[tier.toUpperCase()])
        );
      })
      .includes(true) || false;

    if (!isCustomerEligible) {
      setModal(
        <PayWall
          title={`This is awkward... 
            Earn more VIM points to unlock this offer.`}
          dealTitle={
            "Here are other offers you might consider"
          }
          offer={offer}
          LoginButton={false}
          checkStatus={true}
          cause={"tier_too_low"}
          theme="modal"
          category="Offer"
          page_tier={offer.tiersEligible.toString()}
          user_tier={user?.customer_group?.name?.toUpperCase()}
        />
      );
      setIsEligible(false);
      return;
    } else {
      setIsEligible(true);
    }
  }, [user, authLoading]);

  useEffect(() => {
    if (refresh) {
      const { queryID } = (location.state as { queryID?: string }) || {};
      trackOfferViewEvent("Product Viewed", {
        ...offer,
        url: location.href,
        eventType: EventType.View,
        queryID: queryID,
        query_id: queryID,
      });
    }
  }, [refresh, location]);

  useEffect(() => {
    if (user) {
      const roles = user?.["https://www.vinomofo.com/roles"] || [];
      if (offer.offerType === "vim" && !roles.includes("vim")) {
        window.location.href = window.location.origin;
      }
    }
  }, [user]);

  if (!isEligible) {
    return modal;
  }

  switch (sku?.skuType) {
    case "MultiWineSKU":
      return (
        <OfferDetailsMixed
          details={offerDetailsMixedPropsFromVinoDataOffer(offer)}
          offer={{ ...offer, url: location.href }}
        />
      );
    case "SingleWineSKU":
      return (
        <OfferDetailsSingle
          details={offerDetailsSinglePropsFromVinoDataOffer(offer)}
          offer={{ ...offer, url: location.href }}
        />
      );
    case "NonWineSKU":
      return (
        <OfferDetailsGiftCard
          details={offerDetailsGiftCardPropsFromVinoDataOffer(offer)}
          offer={{ ...offer, url: location.href }}
        />
      );
    default:
      return null;
  }
};
